import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { Button } from "@natera/form";

import { SpinnerView } from "@app/components";
import { PreBillAPIContext } from "@app/provider";
import {
  PreBillTestTypes,
  PaymentChoice,
  SendPaymentChoiceInput,
} from "@app/preBill/models";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import PaymentDetails, { ITest } from "./paymentDetails";
import { usePreBillHeapEvent } from "@app/preBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import "./paymentForm.scss";
import { formatAmountInDollars } from "@app/preBill/utils";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  paymentFormSummaryTitle: {
    id: "paymentFormSummaryTitle",
    defaultMessage: "Order Summary",
  },
  paymentFormPanoromaTestDescription: {
    id: "paymentFormPanoromaTestDescription",
    defaultMessage: "Noninvasive prenatal testing (NIPT)",
  },
  paymentFormHorizonTestDescription: {
    id: "paymentFormHorizonTestDescription",
    defaultMessage: "Advanced carrier screening",
  },
  paymentFormEmpowerTestDescription: {
    id: "paymentFormEmpowerTestDescription",
    defaultMessage: "Hereditary cancer screening",
  },
  paymentFormSaveButton: {
    id: "paymentFormSaveButton",
    defaultMessage: "Pay Now",
  },
  paymentFormBackButton: {
    id: "paymentFormBackButton",
    defaultMessage: "Back",
  },
});

interface PaymentFormProps {
  onSave: (status: "success" | "failure") => void;
  onBack: () => void;
}

const PaymentForm: FC<PaymentFormProps> = ({ onSave, onBack }) => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const {
    apiProviderState: { orderEstimate },
  } = useContext(PreBillAPIContext);
  const [tests, setTests] = useState<ITest[]>([]);
  const stripe = useStripe();
  const elements = useElements();
  const {
    apiProviderState: { customerSetupIntent },
    sendPaymentChoice,
  } = useContext(PreBillAPIContext);
  const { trackPreBillHeapEvent } = usePreBillHeapEvent();

  useEffect(() => {
    formTestsList();
  }, [orderEstimate?.testType]);

  const formTestsList = () => {
    const testsList = orderEstimate?.testType?.map(
      (testName: PreBillTestTypes, index: number) => {
        const descriptions = {
          [PreBillTestTypes.PANORAMA]: formatMessage(
            messages.paymentFormPanoromaTestDescription
          ),
          [PreBillTestTypes.HORIZON]: formatMessage(
            messages.paymentFormHorizonTestDescription
          ),
          [PreBillTestTypes.EMPOWER]: formatMessage(
            messages.paymentFormEmpowerTestDescription
          ),
          [PreBillTestTypes.PANORAMA_HORIZON]: `${formatMessage(
            messages.paymentFormPanoromaTestDescription
          )} & ${formatMessage(messages.paymentFormHorizonTestDescription)}`,
        };

        const test: ITest = {
          id: index + 1,
          name: testName,
          description:
            descriptions[testName.toUpperCase() as PreBillTestTypes] ||
            formatMessage(messages.paymentFormPanoromaTestDescription),
        };
        return test;
      }
    );
    testsList && setTests(testsList);
  };

  const handleSaveButtonClick = async () => {
    if (!stripe || !elements) {
      return;
    }
    trackPreBillHeapEvent({ eventKey: HEAP_EVENTS.upp_pb_click_save_payment });
    setLoading(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    if (error) {
      onSave("failure");
    } else {
      const paymentChoicePayload: SendPaymentChoiceInput = {
        stripeCustomerId: customerSetupIntent?.customer as string,
        paymentMethodId: setupIntent?.payment_method as string,
        paymentChoice: PaymentChoice.CASHPAY,
      };
      try {
        await sendPaymentChoice(paymentChoicePayload);
        onSave("success");
      } catch {
        logMsg("error", "prebill.payment", "Error sending payment choice");
        onSave("failure");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <div className="payment-form__container">
        <PaymentDetails
          title={formatMessage(messages.paymentFormSummaryTitle)}
          tests={tests}
          amount={formatAmountInDollars(
            orderEstimate?.totalPrePayAmountProposed as number
          )}
        />
        <div className="payment-form__body">
          <PaymentElement />
        </div>
        <div className="stepper__actions">
          <ButtonNoShadow
            id="ncs-payment__save-button"
            className="stepper_actions_button"
            onClick={handleSaveButtonClick}
            raised
          >
            {formatMessage(messages.paymentFormSaveButton)}
          </ButtonNoShadow>
          <Button
            id="ncs-payment__back-button"
            className="stepper_actions_button"
            onClick={onBack}
            outlined
          >
            {formatMessage(messages.paymentFormBackButton)}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PaymentForm;
